<template>
    <div class="input">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <label v-if="label" class="input__label" v-html="label"></label>

        <div class="input__inner">
            <input
                :id="uniqueId"
                v-model="state.inputValue"
                class="input__inner__text"
                :class="{
                    'input__inner__text--has-error':
                        (displayErrorsIfAny && v$ && v$.$error) || customError?.value != null,
                    'validated-green': validateGreen,
                }"
                :type="type"
                :placeholder="placeholder"
                :disabled="disabled"
                autocomplete="false"
                @input="updateInputValue($event.target.value)"
                @blur="toggleDisplayErrorsIfAny(true)"
            />
            <div v-show="activeInpLoader" :class="{ input__loader: activeInpLoader }">
                <span class="spinner"></span>
            </div>
            <span v-if="icon && icon.length > 0" :class="`input__inner__icon icon-${icon}`"></span>
        </div>
        <div v-if="displayErrorsIfAny && v$ && v$?.inputValue?.$error">
            <span v-if="v$.inputValue.required" class="input--has-error">Fältet är obligatoriskt</span>
            <span v-if="v$.inputValue.isEmail" class="input--has-error">Ogiltig e-postadress</span>
            <span v-if="v$.inputValue.isSwedishSSN" class="input--has-error"
                >Ogiltigt personnummer, ange enligt YYYYMMDD-XXXX</span
            >

            <span v-if="v$.inputValue.isForeignSSN && validationPattern" class="input--has-error"
                >Ogiltigt Utländskt ID nummer, ange enligt {{ validationPattern }}</span
            >
            <span v-if="v$.inputValue.isSwedishOrgNr && validationPattern" class="input--has-error"
                >Ogiltigt organisationsnummer, ange enligt {{ validationPattern }}</span
            >
            <span v-if="v$.inputValue.isForeignSSN && !validationPattern" class="input--has-error"
                >Ogiltigt Utländskt ID nummer (minst 6 tecken)</span
            >
            <span v-if="v$.inputValue.isSwedishOrgNr && !validationPattern" class="input--has-error"
                >Ogiltigt organisationsnummer, ange enligt XXXXXX-XXXX</span
            >
            <span
                v-if="
                    v$.inputValue.isSwedishPhoneNr &&
                    state.inputValue &&
                    (state.inputValue.startsWith('07') || state.inputValue.startsWith('+46'))
                "
                class="input--has-error"
                >Ogiltigt mobilnummer, ange enligt 07XXXXXXXX eller +467XXXXXXXX</span
            >
            <span
                v-if="
                    v$.inputValue.isSwedishPhoneNr &&
                    state.inputValue &&
                    !(state.inputValue.startsWith('07') || state.inputValue.startsWith('+46'))
                "
                class="input--has-error"
                >Ogiltigt mobilnummer, måste börja med plus och landsnummer (t.ex +47XXXXXXXX)</span
            >
        </div>
        <span v-if="customError" class="input--has-error">{{ customError }}</span>
    </div>
</template>

<script>
    import { computed, watchEffect, reactive, ref, onMounted } from 'vue';
    import useVuelidate from '@vuelidate/core';
    import { required } from '@vuelidate/validators';
    import Email from '@validators/email';
    import SwedishSSN from '@validators/swedish-ssn';
    import ForeignSSN from '@validators/foreign-ssn';
    import SwedishOrgNr from '@validators/swedish-org';
    import SwedishPhoneNumber from '@validators/swedish-phone_nr';

    export default {
        props: {
            modelValue: {
                type: String,
                required: false,
            },
            validators: {
                type: String,
                required: false,
            },
            validationPattern: {
                type: String,
                required: false,
            },
            type: {
                type: String,
                required: false,
                default: 'text',
            },
            label: {
                type: String,
                required: false,
            },
            placeholder: {
                type: String,
                required: false,
            },
            icon: {
                type: String,
                required: false,
            },
            disabled: {
                type: Boolean,
                required: false,
                default: false,
            },
            validateGreen: {
                type: Boolean,
                required: false,
                default: false,
            },
            activeInpLoader: {
                type: Boolean,
                required: false,
                default: false,
            },
            callback: {
                type: Function,
                required: false,
            },
            focus: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        emits: ['update:modelValue'],
        setup(props, { emit }) {
            let timeout = null;
            let v$ = null;

            const isSwedishSSN = (value) => SwedishSSN.validate(value);
            const isForeignSSN = (value) => ForeignSSN.validateMatch(value, props.validationPattern);
            const isSwedishOrgNr = (value) =>
                props.validationPattern && props.validationPattern != 'xxxxxx-xxxx'
                    ? ForeignSSN.validateMatch(value, props.validationPattern)
                    : SwedishOrgNr.validate(value);
            const isSwedishPhoneNr = (value) => SwedishPhoneNumber.validate(value);
            const isEmail = (value) => Email.validate(value);
            const hasValidation = props.validators !== undefined ? true : false;
            const state = reactive({
                inputValue: '',
            });
            const displayErrorsIfAny = ref(true);
            const customError = ref(null);
            const uniqueId = 'InputID' + Date.now();

            watchEffect(() => {
                if (props.modelValue && props.modelValue.length > 0) {
                    state.inputValue = props.modelValue;
                } else {
                    state.inputValue = null;
                }
                customError.value = null;
            });

            const reset = () => {
                if (hasValidation) {
                    state.inputValue = '';
                    v$.value?.inputValue?.$reset();
                    v$.value.$error = false;
                }
            };

            const touch = () => {
                v$.value?.inputValue?.$touch();

                if (!v$.value.$invalid && props.callback) {
                    props.callback();
                }
            };

            const updateInputValue = (value) => {
                emit('update:modelValue', value);

                if (hasValidation) {
                    touch();

                    toggleDisplayErrorsIfAny(false);

                    if (timeout) clearTimeout(timeout);

                    timeout = setTimeout(() => {
                        if (v$.value.$invalid) {
                            toggleDisplayErrorsIfAny(true);
                        }
                    }, 1000);
                }
            };

            const toggleDisplayErrorsIfAny = (flag) => {
                displayErrorsIfAny.value = flag;
            };

            const setCustomError = (errorText) => {
                customError.value = errorText;
            };

            if (hasValidation) {
                const validationRules = computed(() => {
                    const RuleRequired = props.validators.split('|').find((r) => r === 'required');
                    const RuleEmail = props.validators.split('|').find((r) => r === 'email');
                    const RuleSsn = props.validators.split('|').find((r) => r === 'ssn');
                    const RuleForeignSsn = props.validators.split('|').find((r) => r === 'foreignssn');
                    const RuleOrgnr = props.validators.split('|').find((r) => r === 'orgnr');
                    const RulePhone = props.validators.split('|').find((r) => r === 'phone');

                    let requiredRoles = {};

                    if (RuleRequired) {
                        requiredRoles.inputValue = { required };
                    }

                    if (RuleEmail) {
                        requiredRoles.inputValue = { isEmail };
                    }

                    if (RuleSsn) {
                        requiredRoles.inputValue = { isSwedishSSN };
                    }

                    if (RuleForeignSsn) {
                        requiredRoles.inputValue = { isForeignSSN };
                    }

                    if (RuleOrgnr) {
                        requiredRoles.inputValue = { isSwedishOrgNr };
                    }

                    if (RulePhone) {
                        requiredRoles.inputValue = { isSwedishPhoneNr };
                    }

                    return requiredRoles;
                });

                v$ = useVuelidate(validationRules, state);
            }
            onMounted(() => {
                if (props.focus) {
                    document.getElementById(uniqueId).focus();
                }
            });

            return {
                v$,
                state,
                hasValidation,
                displayErrorsIfAny,
                touch,
                reset,
                updateInputValue,
                toggleDisplayErrorsIfAny,
                setCustomError,
                customError,
                uniqueId,
            };
        },
        watch: {
            inputValue() {
                if (this.hasValidation) {
                    this.touch();
                }
            },
        },
    };
</script>

<style scoped lang="scss">
    .validated-green {
        display: block;
        margin: 10px 0 0 5px;
        text-align: left;
        background-color: rgba(93, 216, 195, 0.8156862745);
    }
</style>
